import ThumbImg from "../media/ThumbImg";
import { useMemo, useRef } from 'react';
import { spaceUnreadTotals, anyDmUnread } from '../../lib/unread';
import { mergeOrder } from './slotReorder';
import { useSlotReorder } from './useSlotReorder';

// Structural subset of ChatLayout's VoidWS — only what the header renders.
type HeaderSpace = { id: string; name: string; avatarUrl?: string | null };

type MobileHeaderProps = {
  voids: HeaderSpace[];
  hiddenSpaces: Record<string, boolean>;
  spaceOrder: string[];
  setSpaceOrder: React.Dispatch<React.SetStateAction<string[]>>;
  unread: Record<string, number>;
  currentVoidId: string;
  dmPageOpen: boolean;
  me: { userId?: string; name?: string; avatarUrl?: string | null };
  fallbackInitial?: string;
  userQuickOpen: boolean;
  friendReqCount: number;
  unreadNotificationCount: number;
  onHome: () => void;
  onOpenDmPage: () => void;
  onSwitchVoid: (id: string) => void;
  onToggleUserQuick: () => void;
  onOpenNotifications: () => void;
};

function MobileHeader({
  voids,
  hiddenSpaces,
  spaceOrder,
  setSpaceOrder,
  unread,
  currentVoidId,
  dmPageOpen,
  me,
  fallbackInitial,
  userQuickOpen,
  friendReqCount,
  unreadNotificationCount,
  onHome,
  onOpenDmPage,
  onSwitchVoid,
  onToggleUserQuick,
  onOpenNotifications,
}: MobileHeaderProps) {
  const hasDmUnread = anyDmUnread(voids, unread);
  const spaceTotals = useMemo(() => spaceUnreadTotals(unread), [unread]);

  // Same visible, saved order as the desktop rail and the Spaces sheet.
  const ordered = voids
    .filter((v) => !String(v.id).startsWith('dm_') && !hiddenSpaces[v.id])
    .sort((a, b) => {
      const ia = spaceOrder.indexOf(a.id);
      const ib = spaceOrder.indexOf(b.id);
      return (ia === -1 ? 1e9 : ia) - (ib === -1 ? 1e9 : ib);
    });
  // Press and hold an icon, then slide it left or right (a plain swipe still scrolls the row).
  const rowRef = useRef<HTMLDivElement>(null);
  const reorder = useSlotReorder({
    axis: 'x',
    ids: ordered.map((s) => s.id),
    scrollRef: rowRef,
    onReorder: (ids) => setSpaceOrder((prev) => mergeOrder(prev, ids)),
  });

  return (
    <header
      className="mobile-chrome sm:hidden fixed top-0 left-0 right-0 z-40 w-full"
      style={{ transform: "translateY(var(--vv-top, 0px))" }}
    >
      <div className="px-4 pt-[calc(env(safe-area-inset-top)+8px)] pb-4 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              className={`flex h-10 w-10 items-center justify-center rounded-full border ${!currentVoidId ? 'border-emerald-400 text-white' : 'border-white/25 text-fg-2 hover:text-white'}`}
              onClick={onHome}
              title="Go to landing"
              aria-label="Go to landing"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                <path d="M3 9l9-7 9 7" />
                <path d="M9 22V12h6v10" />
              </svg>
            </button>
            <button
              className={`relative flex h-10 w-10 items-center justify-center rounded-full border ${
                dmPageOpen ? 'border-emerald-400 text-white' : 'border-white/25 text-fg-2 hover:text-white'
              }`}
              onClick={onOpenDmPage}
              title="Open Direct Messages"
              aria-label="Open Direct Messages"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
              </svg>
              {hasDmUnread ? <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500"></span> : null}
            </button>
          </div>
          <div className="min-w-0 text-center">
            <div ref={rowRef} className="overflow-x-auto flex items-center justify-center gap-2 px-1.5 py-1.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {ordered.map((v, idx) => {
                const active = v.id === currentVoidId;
                const spaceUnread = spaceTotals[v.id] || 0;
                const dragging = reorder.dragId === v.id;
                return (
                  <div key={v.id} ref={reorder.itemRef(idx)} className="relative shrink-0" style={reorder.itemStyle(idx, v.id)}>
                    <button
                      {...reorder.itemProps(idx, v.id)}
                      onClick={() => { if (reorder.consumeClick()) return; onSwitchVoid(v.id); }}
                      aria-roledescription="reorderable space"
                      aria-label={v.name}
                      className="relative block"
                    >
                      <div className={`h-8 w-8 rounded-full border ${active || dragging ? 'border-emerald-400 ring-2 ring-emerald-500/30' : 'border-white/15'} bg-white/5 flex items-center justify-center overflow-hidden`}>
                        {v.avatarUrl ? <ThumbImg w={56} src={v.avatarUrl} alt="" className="h-full w-full object-cover" draggable={false} /> : <span className="text-meta font-medium">{(v.name?.[0] || '?').toUpperCase()}</span>}
                      </div>
                    </button>
                    {spaceUnread > 0 ? <span className="pointer-events-none absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500"></span> : null}
                  </div>
                );
              })}
            </div>
          </div>
          {/* Badges live on a relative wrapper, not inside the button: a <button>
              nested in a <button> is invalid HTML and made a tap on the amber
              notification badge also fire the quick-settings toggle. */}
          <div className="relative shrink-0">
            <button
              className={`h-9 w-9 rounded-full border ${userQuickOpen ? 'border-emerald-500 shadow-[0_8px_24px_rgba(16,185,129,0.35)]' : 'border-white/15'} bg-black/40 overflow-hidden flex items-center justify-center`}
              title="Quick settings"
              aria-label="Quick settings"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onToggleUserQuick(); }}
            >
              <span className="h-full w-full rounded-full overflow-hidden bg-neutral-800 border border-neutral-700 flex items-center justify-center">
                {me?.avatarUrl ? (
                  <ThumbImg w={56} src={me.avatarUrl} alt={me?.name || 'me'} className="h-full w-full object-cover" />
                ) : (
                  <span className="text-meta text-fg-2">{(me?.name?.[0] || fallbackInitial || '?').toUpperCase()}</span>
                )}
              </span>
            </button>
            {friendReqCount > 0 && (
              <span className="pointer-events-none absolute -top-1 -right-1 min-w-[16px] px-1 py-0.5 rounded-full bg-emerald-500 text-meta font-semibold text-black ring-2 ring-neutral-900 text-center leading-none">
                {Math.min(99, friendReqCount)}
              </span>
            )}
            {unreadNotificationCount > 0 && (
              <button
                className="absolute -top-1 -left-1 min-w-[16px] px-1 py-0.5 rounded-full bg-amber-400 text-meta font-medium text-black ring-2 ring-neutral-900 text-center leading-none"
                onClick={(e) => { e.stopPropagation(); onOpenNotifications(); }}
                title="View notifications"
                aria-label="View notifications"
              >
                {Math.min(99, unreadNotificationCount)}
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default MobileHeader;
